import api, { Branch } from '@api';
import { RoutedDialog, RoutedDialogProps } from '@tsp-ui/core/components';
import {
    replaceItemById, useAsyncEffect, usePageMessage, useParams
} from '@tsp-ui/core/utils';
import { AdminRouteParams } from '@views/admin/components/AdminPageTemplate';
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomerDetailContext } from '../../CustomerDetailPage';

import AddEditBranchDialogContent from './components/AddEditBranchDialogContent';
import BranchesDialogContent from './components/BranchesDialogContent';


export type BranchDialogProps = Omit<RoutedDialogProps, 'title' | 'children'>;

export default function BranchDialog(props: BranchDialogProps) {
    const { closeTo } = props;
    const { customerID, '*': add } = useParams<AdminRouteParams<'customer'>>();
    const navigate = useNavigate();
    const pageMessage = usePageMessage();

    const [ defaultValues, setDefaultValues ] = useState<Branch>();
    const [ branches, setBranches ] = useState<Branch[]>([]);
    const [ loading, setLoading ] = useState(false);

    const onEdit = (editedBranch: Branch) => {
        setBranches(replaceItemById(branches, editedBranch));
        setDefaultValues(undefined);

        pageMessage.success('Branch saved');
    };

    const { customer, updateCustomer } = useContext(CustomerDetailContext);

    const onCreate = (branch: Branch) => {
        const updatedBranches = [ ...branches, branch ];

        setBranches(updatedBranches);
        customer && updateCustomer({
            ...customer,
            branches: updatedBranches
        });

        pageMessage.success('Branch created');
    };

    const onDone = () => {
        navigate(`${closeTo}/branches`);
        setDefaultValues(undefined);
    };

    useAsyncEffect(useCallback(async () => {
        if (customerID) {
            setLoading(true);

            try {
                setBranches(await api.customer.branches.getBranches(customerID));
            } catch (error) {
                pageMessage.handleApiError('An error occurred while fetching the branches', error);
            }

            setLoading(false);
        }
    }, [ customerID, pageMessage ]));

    return (
        <RoutedDialog
            title={defaultValues
                ? 'Edit branch'
                : add
                    ? 'Add branch'
                    : 'Branches'}
            loading={loading}
            {...props}
        >
            {defaultValues || add ? (
                <AddEditBranchDialogContent
                    defaultValues={defaultValues}
                    onEdit={onEdit}
                    onCreate={onCreate}
                    onDone={onDone}
                />
            ) : (
                <BranchesDialogContent
                    onEditClick={setDefaultValues}
                    branches={branches}
                />
            )}
        </RoutedDialog>
    );
}
